import { useEffect } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useUser } from "./context/UserContext";
import { useURLContext } from "./context/URLContext";
import UserLogin from "./pages/user_component/UserLogin";
import Configuration from "./pages/configuration/ConfigurationPage";
import Analyse from "./pages/analyse/AnalysePage";
import Export from "./pages/export/ExportPage";
import Layout from "./components/layout/Layout";
import Create from "./pages/create/CreatePage";
import LibraryPage from "./pages/library/LibraryPage";
import { BatchContextProvider } from "./context/batch/BatchContext";
import SwaggerPage from "./pages/SwaggerPage";
import { AnalysisPlanContextProvider } from "context/batch/AnalysisPlanContext";
import AnalysisPlanPage from "./pages/create/AnalysisPlanPage";
import { KGAnalysisPlanContextProvider } from "context/batch/KGAnalysisPlanContext";
import { KGBatchContextProvider } from "context/batch/KGBatchContext";
import IntegrationPage from "./pages/integration/IntegrationPage";
import AnalysisPlan from "containers/analysis-plan/AnalysisPlan";
import Agents from "pages/agents/Agents";
import EvaDetailPage from "./pages/eva/EvaDetailPage";
import EvaListPage from "./pages/eva/EvaListPage";
import EvaAgentCreatePage from "pages/eva/EvaAgentCreatePage";
import DarwinAgentEvaDetailPage from "./pages/new_darwin_eva/EvaAgentDetailPage";
import EvaToolDetailPage from "./pages/new_darwin_eva/EvaToolDetailPage";
import DarwinEvaListPage from "./pages/new_darwin_eva/EvaListPage";
import DarwinEvaAgentCreatePage from "pages/new_darwin_eva/EvaAgentCreatePage";
import ProtectedEvaRoute from "./pages/eva/ProtectedEvaRoute";
import ChatPage from "pages/chat/ChatPage";
import AnalystPage from "pages/analyst/AnalystPage";
import ChatSessionPage from "pages/chat/ChatSessionPage";
import { AnalystChatContextProvider } from "context/AnalystChatContext";
import DataConfiguratorContainer from "containers/data_query/DataConfiguratorContainer";
import { init, AuthType } from "@thoughtspot/visual-embed-sdk";
import "katex/dist/katex.min.css";

function App() {
  // Access isLoggedIn and reload function from the user context
  const { isLoggedIn, tsLoginUsername, tsLoginPassword, tsLoginUrl, tsBaseUrl, isLoading, setIsTSInitialized } = useUser();
  const { setPreLoginUrl } = useURLContext();
  const location = useLocation();

  // Login to ThoughtSpot when app loads and user is authenticated
  useEffect(() => {
    const loginToThoughtSpot = async () => {
      // Only proceed if user is logged in and we have ThoughtSpot credentials
      if (!isLoggedIn || !tsLoginUsername || !tsLoginPassword || isLoading) {
        return;
      }
      setIsTSInitialized(false);

      try {
        // Step 1: Login to ThoughtSpot
        const response = await fetch(
          tsLoginUrl,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "X-Requested-With": "XMLHttpRequest"
            },
            credentials: "include",
            body: JSON.stringify({
              username: tsLoginUsername,
              password: tsLoginPassword,
              rememberMe: true
            })
          }
        );

        if (!response.ok) {
          console.error("ThoughtSpot login failed: " + response.status + " " + response.statusText);
          return;
        }


        // Step 2: Initialize ThoughtSpot SDK
        init({
          thoughtSpotHost: tsBaseUrl,
          authType: AuthType.None,
        });

        setIsTSInitialized(true);

      } catch (err) {
        console.error("ThoughtSpot login error: " + err);
        setIsTSInitialized(false);
      }
    };

    loginToThoughtSpot();
  }, [isLoggedIn, tsLoginUsername, tsLoginPassword, isLoading]);

  // Store the current URL when user is not logged in (except for root path)
  useEffect(() => {
    if (!isLoggedIn) {
      const fullPath = location.pathname + location.search;
      // Only store if it's not the root path to avoid unnecessary redirects
      if (fullPath !== "/") {
        setPreLoginUrl(fullPath);
      }
    }
  }, [isLoggedIn, location.pathname, location.search, setPreLoginUrl]);

  const authenticatedRoutes = (
    <AnalystChatContextProvider>
      <BatchContextProvider>
        <KGBatchContextProvider>
          <AnalysisPlanContextProvider>
            <KGAnalysisPlanContextProvider>
              <Layout>
                <Routes>
                  <Route path="/" element={<ChatPage />} />
                  <Route path="/mkg/*" element={<Configuration />} />
                  <Route path="/analyze/:id?" element={<Analyse />} />
                  <Route path="/chat-session" element={<ChatSessionPage />} />
                  <Route path="/chat/new" element={<ChatPage />} />
                  <Route path="/chat/:id?" element={<ChatPage />} />
                  <Route path="/analyst/:id?" element={<AnalystPage />} />
                  <Route path="/analyst/:id/jobs" element={<AnalystPage />} />
                  <Route path="/analyst/:id/jobs/:jobId" element={<AnalystPage />} />
                  <Route path="/export" element={<Export />} />
                  <Route path="/create" element={<Create />} />
                  <Route path="/data-query/:id?" element={<DataConfiguratorContainer />} />
                  <Route path="/analysisPlan" element={<AnalysisPlanPage />} />
                  <Route path="/plan/:id?" element={<AnalysisPlan />} />
                  <Route path="/plan/:id/insights" element={<AnalysisPlan />} />
                  <Route path="/plan/:id/runs" element={<AnalysisPlan />} />
                  <Route path="/plan/:id/schedule" element={<AnalysisPlan />} />
                  <Route path="/agent/:id?" element={<Agents />} />
                  <Route path="/agent/:id/runs" element={<Agents />} />
                  <Route path="/integration" element={<IntegrationPage />} />
                  <Route path="/docs/swagger" element={<SwaggerPage />} />
                  <Route path="/library" element={<LibraryPage />} />
                  <Route path="/old-eva" element={<ProtectedEvaRoute><EvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/old-eva/agent/create" element={<ProtectedEvaRoute><EvaAgentCreatePage /></ProtectedEvaRoute>} />
                  <Route path="/old-eva/:agent_id" element={<ProtectedEvaRoute><EvaDetailPage /></ProtectedEvaRoute>} />
                  <Route path="/eva" element={<ProtectedEvaRoute><Navigate to="/eva/agents" replace /></ProtectedEvaRoute>} />
                  <Route path="/eva/agents" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/tools" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/playground" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/experiments" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/chat/:id" element={<ProtectedEvaRoute><DarwinEvaListPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/agent/create" element={<ProtectedEvaRoute><DarwinEvaAgentCreatePage /></ProtectedEvaRoute>} />
                  <Route path="/eva/:agent_id" element={<ProtectedEvaRoute><DarwinAgentEvaDetailPage /></ProtectedEvaRoute>} />
                  <Route path="/eva/tool/:tool_id" element={<ProtectedEvaRoute><EvaToolDetailPage /></ProtectedEvaRoute>} />
                  <Route path="*" element={<Navigate to="/" />} />{" "}
                </Routes>
              </Layout>
            </KGAnalysisPlanContextProvider>
          </AnalysisPlanContextProvider>
        </KGBatchContextProvider>
      </BatchContextProvider>
    </AnalystChatContextProvider>
  );

  const unauthenticatedRoutes = (
    <Routes>
      <Route path="/*" element={<UserLogin />} />{" "}
    </Routes>
  );

  return (
    <div className="App">
      {isLoggedIn ? authenticatedRoutes : unauthenticatedRoutes}
    </div>
  );
}

export default App;